import { useCallback, useState } from "react";
import {
  Image,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import { Check } from "lucide-react-native";
import { ActionBanner, colors, Dialog, Row } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { EMBER } from "../../../../onboarding/theme";
import { GroupedCard } from "../components";
import { ICONS } from "../icons";
import type { PersonalMedicalData } from "../types";
import type { MedicalFertilityDestination } from "./CareContext";

const ILLUSTRATION_SIZE = 26;

const BLOOD_TYPE_OPTIONS = [
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
] as const;

type Props = {
  data: PersonalMedicalData;
  onNavigate: (destination: MedicalFertilityDestination) => void;
};

function DetailIllustration({ source }: { source: ImageSourcePropType }) {
  return (
    <Image
      source={source}
      resizeMode="contain"
      style={{ width: ILLUSTRATION_SIZE, height: ILLUSTRATION_SIZE }}
    />
  );
}

export default function PersonalMedicalInfo({ data, onNavigate }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PersonalMedicalInfo");
  const updatePatient = useQueuedPatientUpdate();
  const [bloodPickerVisible, setBloodPickerVisible] = useState(false);
  const [bloodSaveError, setBloodSaveError] = useState(false);

  const allergiesLabel =
    data.allergies.length > 0
      ? data.allergies.join(", ")
      : data.hasAllergies === false
        ? t("you.medical_details.allergies_none")
        : t("you.medical_details.allergies_add");

  const saveBloodType = useCallback(
    async (bloodType: (typeof BLOOD_TYPE_OPTIONS)[number] | null) => {
      if (updatePatient.isPending) return;
      setBloodSaveError(false);
      try {
        await updatePatient.mutateAsync({ bloodType });
        setBloodPickerVisible(false);
      } catch {
        setBloodSaveError(true);
      }
    },
    [updatePatient],
  );

  return (
    <View>
      <GroupedCard>
        <Row
          surface="plain"
          leading={<DetailIllustration source={ICONS.blood} />}
          leadingSize={ILLUSTRATION_SIZE}
          title={t("you.medical_details.blood_type_label")}
          subtitle={
            data.bloodType ?? t("you.medical_details.blood_type_placeholder")
          }
          action="you.medical_details.blood_type_label"
          onPress={() => {
            setBloodSaveError(false);
            setBloodPickerVisible(true);
          }}
          disabled={updatePatient.isPending}
          gap={14}
          paddingHorizontal={16}
          paddingVertical={12}
          borderRadius={0}
          titleStyle={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
          subtitleStyle={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textLight,
          }}
          style={{ borderRadius: 0 }}
        />
        <Row
          surface="plain"
          leading={<DetailIllustration source={ICONS.alert} />}
          leadingSize={ILLUSTRATION_SIZE}
          title={t("you.medical_details.allergies_label")}
          subtitle={allergiesLabel}
          action="you.medical_details.allergies_label"
          onPress={() => onNavigate({ screen: "AllergiesFlow" })}
          gap={14}
          paddingHorizontal={16}
          paddingVertical={12}
          borderRadius={0}
          titleStyle={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
          subtitleStyle={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textLight,
          }}
          style={{
            borderRadius: 0,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        />
      </GroupedCard>

      {bloodSaveError ? (
        <View style={{ marginTop: -8, marginBottom: 20 }}>
          <ActionBanner
            variant="important"
            message={t("you.medical_details.blood_type_save_failed")}
          />
        </View>
      ) : null}

      <Dialog
        visible={bloodPickerVisible}
        onClose={() => setBloodPickerVisible(false)}
        layout="centered"
        dismissOnBackdropTap={!updatePatient.isPending}
        contentStyle={{ paddingTop: 28, paddingBottom: 24 }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            fontWeight: "600",
            color: EMBER.colors.text,
            marginBottom: 16,
            textAlign: "center",
          }}
        >
          {t("you.medical_details.blood_type_picker_title")}
        </Text>
        <View style={{ gap: 6 }}>
          {[null, ...BLOOD_TYPE_OPTIONS].map((option) => {
            const selected = option === data.bloodType;
            const label =
              option ?? t("you.medical_details.blood_type_placeholder");
            return (
              <Pressable
                key={option ?? "not-set"}
                accessibilityRole="button"
                accessibilityState={{
                  selected,
                  disabled: updatePatient.isPending,
                }}
                disabled={updatePatient.isPending}
                onPress={() => {
                  capturePress(pressLabel(label), "blood_type_option");
                  void saveBloodType(option);
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingHorizontal: 14,
                  paddingVertical: 11,
                  borderRadius: 10,
                  backgroundColor: selected
                    ? `${EMBER.colors.warm}12`
                    : "transparent",
                  borderWidth: 1,
                  borderColor: selected
                    ? `${EMBER.colors.warm}30`
                    : "transparent",
                  opacity: updatePatient.isPending ? 0.55 : 1,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 1,
                    fontSize: 15,
                    fontWeight: selected ? "600" : "400",
                    color: selected ? EMBER.colors.warm : EMBER.colors.text,
                  }}
                >
                  {label}
                </Text>
                {selected ? (
                  <Check size={16} color={EMBER.colors.warm} strokeWidth={2} />
                ) : null}
              </Pressable>
            );
          })}
        </View>
      </Dialog>
    </View>
  );
}
